import { Dispatch, SetStateAction, useMemo } from 'react'
import { DatesInfo } from '../../../../interfaces/baseCalculation/DatesInfo'
import {
  CellClassParams,
  KeyCreatorParams,
  ValueFormatterParams,
  ValueParserParams,
} from 'ag-grid-community'
import { numberFormatter } from '../../../../utils/numberFormatter'
import { getRowStyle } from '../../../../utils/getRowStyle'
import useCheckIfSheetEditable from '../customHooks/useCheckIfSheetEditable'
import { numericValueParser } from '../helper/numericValueParser'
import { RowStyle } from '../../../../interfaces/grids/RowStyle'
import CommentCellRenderer from '../cellRenderers/CommentCellRenderer/CommentCellRenderer'
import { TotalPrognosisCellRenderer } from '../cellRenderers/TotalPrognosisCellRenderer'
import { useProjektprognosEditingContext } from '../../context/ProjektprognosEditingContext'
import ConditionalAggCellRenderer from '../cellRenderers/ConditionalAggCellRenderer'
import { ReadOnlyLargeTextCellEditor } from '../cellEditors/ReadOnlyLargeTextCellEditor'
import { useCommentAccess } from '../../customHooks/useCommentAccess'

interface CashflowColDefs {
  datesInfo: DatesInfo
  lockedBudget: boolean
}

export const useCashflowColDefs = ({
  datesInfo,
  lockedBudget,
}: CashflowColDefs) => {
  const checkIfEditable = useCheckIfSheetEditable()
  const { easterEggMode, selectedPeriodReport } =
    useProjektprognosEditingContext()
  const { canOpenCommentEditor, canEditComment } = useCommentAccess({
    periodReport: selectedPeriodReport,
  })

  const cashflowTotalsColDef: object[] = useMemo(() => {
    const rowStyle: RowStyle = { backgroundColor: '#d9dcde', fontWeight: '' }

    return [
      {
        headerName: 'Totaler',
        field: 'totals',
        children: [
          {
            field: 'grandParentDisplayName',
            hide: true,
            rowGroup: true,
            keyCreator: (params: KeyCreatorParams) => {
              return (
                params.node?.data.grandParentDisplayName ??
                params.node?.data.displayName
              )
            },
          },
          {
            field: 'parentDisplayName',
            hide: true,
            rowGroup: true,
          },
          {
            headerName: !lockedBudget ? 'Budget' : 'Budget*',
            field: 'budget',
            flex: 1,
            type: 'numericColumn',
            aggFunc: 'customSum',
            cellRenderer: ConditionalAggCellRenderer,
            valueFormatter: (params: ValueFormatterParams) =>
              numberFormatter(params, easterEggMode),
            cellStyle: (params: CellClassParams) =>
              getRowStyle(params, { ...rowStyle }),
            editable: !lockedBudget && checkIfEditable,
            valueParser: (params: ValueParserParams) =>
              numericValueParser(params),
          },
          {
            headerName: 'Utfall',
            field: 'totalOutcome',
            flex: 1,
            type: 'numericColumn',
            aggFunc: 'customSum',
            cellRenderer: ConditionalAggCellRenderer,
            valueFormatter: (params: ValueFormatterParams) =>
              numberFormatter(params, easterEggMode),
            cellStyle: (params: CellClassParams) =>
              getRowStyle(params, { ...rowStyle }),
          },
          {
            headerName: 'Föreg. prognos',
            field: 'previousTotalPrognosis',
            flex: 1,
            type: 'numericColumn',
            aggFunc: 'customSum',
            cellRenderer: ConditionalAggCellRenderer,
            cellRendererParams: {
              innerRenderer: TotalPrognosisCellRenderer,
            },

            valueFormatter: (params: ValueFormatterParams) =>
              numberFormatter(params, easterEggMode),
            cellStyle: (params: CellClassParams) =>
              getRowStyle(params, { ...rowStyle }),
            headerTooltip: 'Föreg. prognos',
          },
          {
            headerName: 'Prognos',
            field: 'totalPrognosis',
            flex: 1,
            type: 'numericColumn',
            aggFunc: 'customSum',
            cellRenderer: ConditionalAggCellRenderer,
            cellRendererParams: {
              innerRenderer: TotalPrognosisCellRenderer,
            },
            valueFormatter: (params: ValueFormatterParams) =>
              numberFormatter(params, easterEggMode),
            cellStyle: (params: CellClassParams) =>
              getRowStyle(params, { ...rowStyle }),
          },
          {
            headerName: 'Diff',
            headerTooltip: 'Prognos - Föreg. prognos',
            field: 'difference',
            flex: 1,
            type: 'numericColumn',
            aggFunc: 'customDiffPrognosis',
            cellRenderer: ConditionalAggCellRenderer,
            valueFormatter: (params: ValueFormatterParams) =>
              numberFormatter(params, easterEggMode),
            cellStyle: (params: CellClassParams) =>
              getRowStyle(params, { ...rowStyle }),
          },
          {
            headerName: 'Kommentar',
            field: 'comment',
            flex: 2,
            cellRenderer: CommentCellRenderer,
            type: 'rightAligned',
            cellStyle: (params: CellClassParams) =>
              getRowStyle(params, { ...rowStyle }),
            editable: canOpenCommentEditor,
            singleClickEdit: true,
            cellEditor: ReadOnlyLargeTextCellEditor,
            cellEditorPopup: true,
            cellEditorParams: {
              maxLength: 500,
              rows: 10,
              cols: 30,
              readOnly: !canEditComment,
              readOnlyMessage:
                'Du har inte behörighet att redigera kommentaren',
            },
          },
        ],
      },
    ]
  }, [
    lockedBudget,
    checkIfEditable,
    canOpenCommentEditor,
    canEditComment,
    easterEggMode,
  ])

  return cashflowTotalsColDef
}
